"use client";

import React, { useEffect, useState } from "react";
import { MapPin, Clock, Building2, ArrowRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const locations = [{
  id: "hq",
  city: "The Estate",
  country: "VOW Grounds",
  address: "777 Serenity Lane, Willow Creek",
  top: "50%",
  left: "50%",
  timezone: "America/New_York",
  isHQ: true
}];
export default function FindUs() {
  const [mounted, setMounted] = useState(false);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  useEffect(() => {
    setMounted(true);
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  const getLocalTime = (timeZone: string) => {
    if (!mounted) return "--:--";
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeStyle: "short",
        timeZone
      }).format(currentTime);
    } catch (e) {
      return "--:--";
    }
  };
  return <section data-section-id="224599" id="find-us" className="w-full py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-6xl font-medium font-default tracking-tight text-primary">
            Find Us
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl font-sans">
            Nestled in a picturesque location, VOW offers an elegant sanctuary for your special day. Explore our estate and plan your visit.
          </p>
        </div>

        <div className="relative w-full max-w-6xl mx-auto aspect-square md:aspect-[21/9] bg-muted rounded-md overflow-hidden shadow-lg border border-border">
          {}
          <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683483/img/vow_estate_map_overlay.png)] bg-cover bg-center opacity-60 grayscale mix-blend-multiply pointer-events-none"></div>

          {}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#C5A05912_1px,transparent_1px),linear-gradient(to_bottom,#C5A05912_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none"></div>

          {}
          {locations.map((loc, i) => <div key={loc.id} data-index={i} className="absolute z-10" style={{
          top: loc.top,
          left: loc.left,
          transform: "translate(-50%, -50%)"
        }}>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative h-auto w-auto p-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-background group" aria-label={`View details for ${loc.city}`}>
                    {}
                    <span className="absolute inline-flex h-8 w-8 rounded-md bg-secondary opacity-20 group-hover:opacity-40 animate-ping"></span>
                    {}
                    <span className="relative inline-flex rounded-md h-4 w-4 bg-secondary shadow-md shadow-secondary/40 ring-2 ring-secondary/30 transition-transform group-hover:scale-110"></span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 bg-popover/95 backdrop-blur-md border-border shadow-xl p-0 overflow-hidden rounded-md" sideOffset={8}>
                  <div className="p-4 border-b border-border bg-accent/20">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-default font-semibold text-lg flex items-center gap-2 text-primary">
                        {loc.city}
                        {loc.isHQ && <span className="text-[10px] uppercase tracking-widest bg-secondary/10 text-secondary px-2 py-0.5 rounded-md font-mono">
                            Main Venue
                          </span>}
                      </h3>
                      <div className="flex items-center text-secondary text-xs font-mono font-medium bg-secondary/5 px-2 py-1 rounded-md">
                        <Clock className="w-3 h-3 mr-1.5" />
                        <span suppressHydrationWarning>
                          {getLocalTime(loc.timezone)}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground font-sans">
                      {loc.country}
                    </p>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex items-start gap-3 text-sm">
                      <MapPin className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                      <span className="text-foreground/80 font-sans leading-tight">
                        {loc.address}
                      </span>
                    </div>
                    <div className="flex items-start gap-3 text-sm">
                      <Building2 className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                      <span className="text-foreground/80 font-sans leading-tight">
                        VOW Grand Ballroom & Gardens
                      </span>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>)}
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div className="p-6 bg-card rounded-md border border-border shadow-sm">
            <h4 className="font-default text-xl mb-2 text-primary">Directions from the City</h4>
            <p className="text-sm text-muted-foreground font-sans leading-relaxed">
              Take the North Expressway to Exit 42. Follow the scenic route along Willow Creek for 5 miles. The VOW estate entrance is marked by our signature gold gates.
            </p>
          </div>
          <div className="p-6 bg-card rounded-md border border-border shadow-sm">
            <h4 className="font-default text-xl mb-2 text-primary">Parking Information</h4>
            <p className="text-sm text-muted-foreground font-sans leading-relaxed">
              Complimentary valet parking is provided for all guests. Self-parking is available in the West Orchard lot with direct shuttle access to the Grand Ballroom.
            </p>
          </div>
        </div>

        <div className="mt-12 flex justify-center">
          <Button asChild size="lg" className="rounded-md px-10 bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest uppercase text-xs">
            <Link to="/BeginYourJourneyPage">
              Begin Your Journey
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}